import { getCLS, getFCP, getFID, getLCP, getTTFB } from 'web-vitals';
import securityReport from '../data/securityReport.json';
import { APP_METADATA } from '../config/appMeta';

export interface SystemReportWebVitals {
  cls: number | null;
  fid: number | null;
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
}

export interface SystemReportResourceStats {
  totalResources: number;
  scriptChunkCount: number;
  totalTransferredKb: number;
  domContentLoadedMs: number | null;
  fullLoadMs: number | null;
}

export interface SystemReportSecurityChecks {
  isHttps: boolean;
  hasMixedContent: boolean;
  authTokenInLocalStorage: boolean;
}

export interface SystemReportDependencySnapshot {
  generatedAt: string;
  summary: { info: number; low: number; moderate: number; high: number; critical: number; total: number };
  directPackageFindings: { name: string; severity: string; fixAvailable: boolean }[];
  totalPackagesAudited: number;
  note: string;
}

export interface SystemReport {
  generatedAt: string;
  environment: string;
  appVersion: string;
  webVitals: SystemReportWebVitals;
  resources: SystemReportResourceStats;
  security: SystemReportSecurityChecks;
  dependencySnapshot: SystemReportDependencySnapshot;
  lazyLoadedRouteCount: number;
}

// The number of pages wired through React.lazy()/Suspense in src/App.tsx.
// Update this if pages are added or removed from the lazy-loaded route list.
const LAZY_LOADED_ROUTE_COUNT = 22;

const collectWebVitals = (): Promise<SystemReportWebVitals> => {
  const vitals: SystemReportWebVitals = { cls: null, fid: null, fcp: null, lcp: null, ttfb: null };

  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      resolve(vitals);
    };

    try {
      getCLS((metric) => { vitals.cls = metric.value; });
      getFCP((metric) => { vitals.fcp = metric.value; });
      getLCP((metric) => { vitals.lcp = metric.value; });
      getTTFB((metric) => { vitals.ttfb = metric.value; });
      // FID only fires after the user's first interaction — may never arrive.
      getFID((metric) => { vitals.fid = metric.value; });
    } catch {
      // web-vitals unsupported in this browser; return whatever defaults we have.
    }

    // Most metrics resolve within a frame or two; cap the wait so the report
    // button never hangs waiting on a metric (like FID) that may never fire.
    window.setTimeout(finish, 700);
  });
};

const collectResourceStats = (): SystemReportResourceStats => {
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const scriptResources = resources.filter((entry) => entry.initiatorType === 'script' || entry.name.endsWith('.js'));
  const totalTransferredBytes = resources.reduce((sum, entry) => sum + (entry.transferSize || 0), 0);

  const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  const nav = navigationEntries[0];

  return {
    totalResources: resources.length,
    scriptChunkCount: scriptResources.length,
    totalTransferredKb: Math.round((totalTransferredBytes / 1024) * 10) / 10,
    domContentLoadedMs: nav ? Math.round(nav.domContentLoadedEventEnd) : null,
    fullLoadMs: nav ? Math.round(nav.loadEventEnd) : null,
  };
};

const collectSecurityChecks = (): SystemReportSecurityChecks => {
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const isHttps = window.location.protocol === 'https:';
  const hasMixedContent = isHttps && resources.some((entry) => entry.name.startsWith('http://'));
  const authTokenInLocalStorage = Boolean(localStorage.getItem('authToken') || localStorage.getItem('token'));

  return { isHttps, hasMixedContent, authTokenInLocalStorage };
};

export const collectSystemReport = async (): Promise<SystemReport> => {
  const [webVitals] = await Promise.all([collectWebVitals()]);

  return {
    generatedAt: new Date().toISOString(),
    environment: process.env.NODE_ENV,
    appVersion: APP_METADATA.version,
    webVitals,
    resources: collectResourceStats(),
    security: collectSecurityChecks(),
    dependencySnapshot: securityReport as SystemReportDependencySnapshot,
    lazyLoadedRouteCount: LAZY_LOADED_ROUTE_COUNT,
  };
};
